import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Icon } from '@/components';

/**
 * In-app QR scanner (rear camera) for venue check-in. Renders a live camera
 * preview and fires `onScan` once with the decoded text. Works inside the LINE
 * webview / mobile browsers over HTTPS via getUserMedia — no native camera-app
 * hop, so the learner stays logged in and check-in completes in one step.
 */
export function QrScanner({ onScan, onCancel }: { onScan: (text: string) => void; onCancel: () => void }) {
  const startedRef = useRef(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const doneRef = useRef(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const scanner = new Html5Qrcode('qr-reader-region', { verbose: false });
    scannerRef.current = scanner;
    scanner
      .start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 232, height: 232 } },
        (text) => {
          if (doneRef.current) return;
          doneRef.current = true;
          onScan(text);
        },
        () => {}, // per-frame decode misses are normal; ignore
      )
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : 'Cannot access the camera'));

    return () => {
      const s = scannerRef.current;
      if (s) s.stop().then(() => s.clear()).catch(() => {});
    };
    // onScan is stable enough for one-shot scanning; the startedRef guard prevents restarts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="card" style={{ padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
      <div style={{ fontSize: 15, fontWeight: 600 }}>Point at the venue QR · สแกนคิวอาร์</div>
      <div id="qr-reader-region" style={{ width: '100%', maxWidth: 300, aspectRatio: '1 / 1', borderRadius: 16, overflow: 'hidden', background: '#000' }} />
      {err ? (
        <p style={{ color: 'var(--danger)', fontSize: 13, textAlign: 'center', margin: 0, lineHeight: 1.5, maxWidth: 280 }}>
          {err}. Allow camera access, or ask a staff member at the desk to mark you present.
        </p>
      ) : (
        <p className="th" style={{ fontSize: 13, color: 'var(--ink-3)', textAlign: 'center', margin: 0 }}>
          จัดคิวอาร์ให้อยู่ในกรอบ · Hold the code inside the frame
        </p>
      )}
      <button className="btn btn-ghost btn-block" onClick={onCancel}><Icon name="x" size={16} /> Cancel</button>
    </div>
  );
}
